import { Box, BoxProps } from '@amzn/awsui-components-core';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { NavigationLink } from 'src/components/generic-components/NavigationLink';
import { RecentsService } from 'src/services/recents/RecentsService';
import { RecentEntityType } from 'src/services/recents/RecentsConstants';

interface AppNameWithLinkProps {
  appName: string;
  appId: string;
  embeddingEnabled?: boolean;
  applicationLink?: string;
  fontSize?: BoxProps.FontSize;
  padding?: BoxProps.Spacing;
}

/**
 * Renders an app name as a navigable link.
 * - Embedded apps navigate to `/all-apps/{appId}` within FinSuite.
 * - External apps open `applicationLink` in a new tab.
 * - Apps with neither render as plain text.
 *
 * Records a visit via RecentsService on click.
 */
const AppNameWithLink: React.FC<AppNameWithLinkProps> = ({ appName, appId, embeddingEnabled, applicationLink, fontSize, padding }) => {
  const navigate = useNavigate();
  const { CognitoSub, Alias } = useAuth();

  const recordVisit = (isExternal: boolean) => {
    RecentsService.recordVisit(CognitoSub, Alias, RecentEntityType.APP, appId, { isExternal });
  };

  if (embeddingEnabled) {
    const url = `/all-apps/${appId}`;
    return (
      <Box variant="awsui-key-label" fontWeight="normal" color="text-status-info" fontSize={fontSize || 'body-m'} padding={padding}>
        <NavigationLink
          href={url}
          onClick={() => {
            recordVisit(false);
            navigate(url);
          }}
        >
          {appName}
        </NavigationLink>
      </Box>
    );
  }

  if (applicationLink) {
    return (
      <Box variant="awsui-key-label" fontWeight="normal" color="text-status-info" fontSize={fontSize || 'body-m'} padding={padding}>
        <NavigationLink
          href={applicationLink}
          onClick={() => {
            recordVisit(true);
            window.open(applicationLink, '_blank', 'noopener,noreferrer');
          }}
        >
          {appName}
        </NavigationLink>
      </Box>
    );
  }

  return (
    <Box variant="awsui-key-label" fontWeight="normal" fontSize={fontSize || 'body-m'} padding={padding}>
      {appName}
    </Box>
  );
};

/**
 * Returns a click handler and href for navigating to an app.
 * Use this when you need the navigation logic without the AppNameWithLink UI.
 */
export const useAppNavigation = (appId: string, embeddingEnabled?: boolean, applicationLink?: string) => {
  const navigate = useNavigate();
  const { CognitoSub, Alias } = useAuth();

  if (embeddingEnabled) {
    const url = `/all-apps/${appId}`;
    return {
      href: url,
      isExternal: false,
      handleClick: () => {
        RecentsService.recordVisit(CognitoSub, Alias, RecentEntityType.APP, appId, { isExternal: false });
        navigate(url);
      }
    };
  }

  if (applicationLink) {
    return {
      href: applicationLink,
      isExternal: true,
      handleClick: () => {
        RecentsService.recordVisit(CognitoSub, Alias, RecentEntityType.APP, appId, { isExternal: true });
        window.open(applicationLink, '_blank', 'noopener,noreferrer');
      }
    };
  }

  return { href: undefined, isExternal: false, handleClick: undefined };
};

export default AppNameWithLink;
